import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Orbit, Rocket, Gauge } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { resolveBackendUrl } from '../lib/utils';

const BACKEND_URL = resolveBackendUrl();
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const OrbitSimulationPage = () => {
  const [loading, setLoading] = useState(false);
  const [simulation, setSimulation] = useState(null);
  const [formData, setFormData] = useState({
    asteroid_name: '',
    semi_major_axis_au: '',
    eccentricity: '',
    inclination_deg: '',
    orbital_period_years: '',
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const loadSampleData = () => {
    setFormData({
      asteroid_name: 'Bennu',
      semi_major_axis_au: '1.126',
      eccentricity: '0.204',
      inclination_deg: '6.035',
      orbital_period_years: '1.195',
    });
    toast.success('Sample orbit data loaded!');
  };

  const handleSimulate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSimulation(null);

    try {
      const response = await axios.post(`${API}/orbit/simulate`, {
        asteroid_name: formData.asteroid_name,
        semi_major_axis_au: parseFloat(formData.semi_major_axis_au),
        eccentricity: parseFloat(formData.eccentricity),
        inclination_deg: parseFloat(formData.inclination_deg),
        orbital_period_years: parseFloat(formData.orbital_period_years),
      });

      setSimulation(response.data);
      toast.success('Orbit simulation completed!');
    } catch (error) {
      console.error('Simulation error:', error);
      toast.error('Simulation failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getOrbitChartData = () => {
    if (!simulation || !simulation.orbit_visualization) return [];
    return simulation.orbit_visualization.map(point => ({
      x: point.x,
      y: point.y,
      angle: point.theta_deg
    }));
  };

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-block mb-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-400 to-pink-600 flex items-center justify-center">
              <Orbit className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold gradient-text mb-3" data-testid="page-title">Orbit Simulation</h1>
          <p className="text-gray-400" data-testid="page-description">Skyfield-powered orbital mechanics and mission planning</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Input Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="glass-card border-white/10" data-testid="simulation-form">
              <CardHeader>
                <CardTitle className="text-white">Orbital Parameters</CardTitle>
                <CardDescription className="text-gray-400">Configure asteroid orbit</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSimulate} className="space-y-4">
                  <div>
                    <Label htmlFor="ast-name" className="text-gray-300">Asteroid Name</Label>
                    <Input
                      id="ast-name"
                      data-testid="input-asteroid-name"
                      value={formData.asteroid_name}
                      onChange={(e) => handleInputChange('asteroid_name', e.target.value)}
                      placeholder="e.g., Bennu"
                      required
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="sma" className="text-gray-300">Semi-Major Axis (AU)</Label>
                    <Input
                      id="sma"
                      data-testid="input-semi-major-axis"
                      type="number"
                      step="0.001"
                      value={formData.semi_major_axis_au}
                      onChange={(e) => handleInputChange('semi_major_axis_au', e.target.value)}
                      placeholder="1.126"
                      required
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="ecc" className="text-gray-300">Eccentricity</Label>
                    <Input
                      id="ecc"
                      data-testid="input-eccentricity"
                      type="number"
                      step="0.001"
                      value={formData.eccentricity}
                      onChange={(e) => handleInputChange('eccentricity', e.target.value)}
                      placeholder="0.204"
                      required
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="inc" className="text-gray-300">Inclination (°)</Label>
                    <Input
                      id="inc"
                      data-testid="input-inclination"
                      type="number"
                      step="0.001"
                      value={formData.inclination_deg}
                      onChange={(e) => handleInputChange('inclination_deg', e.target.value)}
                      placeholder="6.035"
                      required
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="period" className="text-gray-300">Orbital Period (years)</Label>
                    <Input
                      id="period"
                      data-testid="input-orbital-period"
                      type="number"
                      step="0.001"
                      value={formData.orbital_period_years}
                      onChange={(e) => handleInputChange('orbital_period_years', e.target.value)}
                      placeholder="1.195"
                      required
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>

                  <div className="flex flex-col gap-3 pt-4">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="cosmic-button"
                      data-testid="simulate-button"
                    >
                      {loading ? 'Simulating...' : 'Simulate Orbit'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={loadSampleData}
                      className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                      data-testid="load-sample-button"
                    >
                      Load Sample
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {/* Results */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="lg:col-span-2 space-y-6"
          >
            {simulation ? (
              <>
                {/* Orbital Parameters */}
                <Card className="glass-card border-white/10" data-testid="orbital-params">
                  <CardHeader>
                    <CardTitle className="text-white">Orbital Characteristics</CardTitle>
                    <CardDescription className="text-gray-400">{simulation.asteroid_name}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="p-3 rounded-lg bg-white/5" data-testid="param-sma">
                        <div className="text-xs text-gray-400 mb-1">Semi-Major Axis</div>
                        <div className="text-lg font-bold text-cyan-400">
                          {simulation.orbital_parameters.semi_major_axis_au} AU
                        </div>
                      </div>
                      <div className="p-3 rounded-lg bg-white/5" data-testid="param-perihelion">
                        <div className="text-xs text-gray-400 mb-1">Perihelion</div>
                        <div className="text-lg font-bold text-purple-400">
                          {simulation.orbital_parameters.perihelion_au} AU
                        </div>
                      </div>
                      <div className="p-3 rounded-lg bg-white/5" data-testid="param-aphelion">
                        <div className="text-xs text-gray-400 mb-1">Aphelion</div>
                        <div className="text-lg font-bold text-yellow-400">
                          {simulation.orbital_parameters.aphelion_au} AU
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Mission Parameters */}
                <Card className="glass-card border-white/10" data-testid="mission-params">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Rocket className="w-5 h-5 text-cyan-400" />
                      Mission Parameters
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 rounded-lg bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20" data-testid="deltav-total">
                        <div className="flex items-center gap-2 mb-2">
                          <Gauge className="w-5 h-5 text-cyan-400" />
                          <span className="text-sm text-gray-400">Total Delta-V Required</span>
                        </div>
                        <div className="text-2xl font-bold text-cyan-400">
                          {simulation.mission_parameters.total_delta_v_km_s} km/s
                        </div>
                      </div>

                      <div className="p-4 rounded-lg bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20" data-testid="transfer-time">
                        <div className="flex items-center gap-2 mb-2">
                          <Orbit className="w-5 h-5 text-purple-400" />
                          <span className="text-sm text-gray-400">Transfer Time</span>
                        </div>
                        <div className="text-2xl font-bold text-purple-400">
                          {simulation.mission_parameters.estimated_transfer_time_years} years
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          ({simulation.mission_parameters.estimated_transfer_time_days} days)
                        </div>
                      </div>

                      <div className="p-3 rounded-lg bg-white/5" data-testid="current-distance">
                        <div className="text-xs text-gray-400 mb-1">Current Distance from Earth</div>
                        <div className="text-lg font-bold text-white">
                          {simulation.current_position.distance_from_earth_au} AU
                        </div>
                      </div>

                      <div className="p-3 rounded-lg bg-white/5" data-testid="orbital-velocity">
                        <div className="text-xs text-gray-400 mb-1">Orbital Velocity</div>
                        <div className="text-lg font-bold text-white">
                          {simulation.current_position.orbital_velocity_km_s} km/s
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Orbit Visualization */}
                <Card className="glass-card border-white/10" data-testid="orbit-visualization">
                  <CardHeader>
                    <CardTitle className="text-white">Orbit Path (2D Projection)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={getOrbitChartData()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis 
                          dataKey="x" 
                          stroke="#6B7280"
                          label={{ value: 'X (AU)', position: 'insideBottom', offset: -5, fill: '#9CA3AF' }}
                        />
                        <YAxis 
                          dataKey="y" 
                          stroke="#6B7280"
                          label={{ value: 'Y (AU)', angle: -90, position: 'insideLeft', fill: '#9CA3AF' }}
                        />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1a1f3a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                          labelStyle={{ color: '#9CA3AF' }}
                        />
                        <Legend wrapperStyle={{ color: '#9CA3AF' }} />
                        <Line 
                          type="monotone" 
                          dataKey="y" 
                          stroke="#00d9ff" 
                          strokeWidth={2}
                          dot={false}
                          name="Orbit Path"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="glass-card border-white/10" data-testid="simulation-placeholder">
                <CardContent className="flex flex-col items-center justify-center py-20">
                  <Orbit className="w-16 h-16 text-gray-600 mb-4" />
                  <p className="text-gray-500 text-center">Enter orbital parameters and simulate to see results</p>
                </CardContent>
              </Card>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default OrbitSimulationPage;
